/*
 * Cooling fee — keeps the chilled-packaging line in step with the cart.
 *
 * A chilled item in the cart must be accompanied by exactly one cooling fee
 * line; the last chilled item leaving must take the fee with it. This replaces
 * an app that carried a hand-maintained list of product IDs, which went stale
 * every time someone launched a frozen product and forgot to add it.
 *
 * WHICH ITEMS COUNT
 * /cart.js gives product_type, vendor, sku, handle and title per line, but
 * never collection membership — so the browser cannot ask the cart "is this
 * line chilled?". Instead it holds the answer alongside: one request per
 * session fetches the chilled collections' variant IDs (see
 * templates/collection.cooling.liquid) and the two lists are intersected here.
 *
 * Matching on the title was the obvious alternative and does not survive
 * contact with the data: of 98 chilled products, 96 carry "(Frozen)" in the
 * English title but only 49 do in German — the translation dropped the marker.
 * The cart returns the localised title, so a title rule would have missed 44%
 * of them in the shop's main market.
 *
 * FAILING SAFE
 * If the ID list cannot be loaded the cart is left completely alone. Adding a
 * fee we cannot justify is bad; removing one that belongs there is worse, and
 * an empty list would look exactly like "nothing is chilled".
 */
(function () {
  const config = window.coolingFee;
  if (!config || !config.feeVariantId || !config.collections) return;

  const FEE = String(config.feeVariantId);
  const HANDLES = config.collections.split(',').map((h) => h.trim()).filter(Boolean);
  const DRY_RUN = config.dryRun === true;
  if (!HANDLES.length) return;

  const SECTIONS = ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
  const CACHE_KEY = `jm:cooling:${HANDLES.join('|')}`;
  // The stepper holds a cart response unread for 420ms so it cannot steal
  // frames from its animation. Sit behind that, and coalesce rapid taps into
  // one pass while we are at it.
  const SETTLE_MS = 600;
  /* Two systems with contradictory rules will fight forever: one adds the fee,
     the other removes it, each seeing the other's write as a cart change. The
     customer watches the total flip once a second and the cart API takes the
     load. Nothing here can prevent that — the other side has to be switched off
     — but it must not be allowed to run. Six writes on a single page load is
     already pathological, so we stop there and say so. */
  const MAX_WRITES = 6;

  let idsPromise = null;
  let running = false;
  let pending = false;
  let ours = false; // our own cart writes must not re-trigger us
  let timer = null;
  let writes = 0;
  let halted = false;

  function root() {
    const base = (window.routes && window.routes.root_url) || '/';
    return base.endsWith('/') ? base : `${base}/`;
  }

  /* One request per collection per session, then sessionStorage. A tab that
     stays open all day therefore misses products added to the collection in
     the meantime — acceptable, since the next visit picks them up and the
     alternative is a request on every cart change. */
  function chilledIds() {
    if (idsPromise) return idsPromise;

    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length) {
          idsPromise = Promise.resolve(new Set(parsed));
          return idsPromise;
        }
      }
    } catch (error) {
      /* private mode, quota, corrupt entry — just fetch instead */
    }

    idsPromise = Promise.all(
      HANDLES.map((handle) =>
        fetch(`${root()}collections/${encodeURIComponent(handle)}?view=cooling`)
          .then((response) => (response.ok ? response.text() : ''))
          .catch(() => '')
      )
    ).then((parts) => {
      const ids = parts
        .join(',')
        .split(',')
        .map((id) => id.trim())
        .filter((id) => /^\d+$/.test(id));

      // Empty means every collection failed. Treat it as "unknown", never as
      // "nothing is chilled" — otherwise one 404 strips the fee from every
      // cart in the shop.
      if (!ids.length) {
        idsPromise = null;
        throw new Error('chilled list empty');
      }

      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(ids));
      } catch (error) {
        /* not being able to cache is not a reason to fail */
      }
      return new Set(ids);
    });

    return idsPromise;
  }

  function cart() {
    return fetch(`${window.routes.cart_url}.js`).then((response) => response.json());
  }

  function write(url, quantity) {
    writes += 1;
    ours = true;
    return fetch(url, {
      ...fetchConfig('javascript'),
      body: JSON.stringify({
        id: FEE,
        quantity,
        sections: SECTIONS,
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .finally(() => {
        ours = false;
      });
  }

  function paint(state) {
    if (!state || !state.sections) return;
    SECTIONS.forEach((id) => {
      const element = document.getElementById(id);
      if (!element || !state.sections[id]) return;
      const fresh = new DOMParser()
        .parseFromString(state.sections[id], 'text/html')
        .querySelector('.shopify-section');
      if (fresh) element.innerHTML = fresh.innerHTML;
    });
  }

  async function reconcile() {
    if (halted) return;
    if (running) {
      pending = true;
      return;
    }
    running = true;

    try {
      const [chilled, state] = await Promise.all([chilledIds(), cart()]);
      const items = state.items || [];

      const needsFee = items.some(
        (item) => String(item.variant_id) !== FEE && chilled.has(String(item.variant_id))
      );
      const feeLine = items.find((item) => String(item.variant_id) === FEE);

      let action = null;
      if (needsFee && !feeLine) action = ['add', 1];
      // The cart UI hides the stepper on this line, but a stale cart from the
      // old app or a direct call to /cart/update.js can still land here.
      else if (needsFee && feeLine.quantity !== 1) action = ['clamp', 1];
      else if (!needsFee && feeLine) action = ['remove', 0];
      if (!action) return;

      if (DRY_RUN) {
        console.info('[cooling] would', action[0], '- items:', items.length, '- fee:', feeLine ? feeLine.quantity : 0);
        return;
      }

      if (writes >= MAX_WRITES) {
        halted = true;
        console.error(
          '[cooling] stopped after ' + MAX_WRITES + ' writes on one page load. Something else is ' +
          'undoing them — most likely another cart app with a different idea of what needs cooling. ' +
          'Switch one of the two off; they cannot both be right.'
        );
        return;
      }

      const url = action[0] === 'add' ? window.routes.cart_add_url : window.routes.cart_change_url;
      paint(await write(url, action[1]));
    } catch (error) {
      // Unknown list or a failed request: leave the cart exactly as it is.
      console.warn('[cooling] skipped:', error && error.message);
    } finally {
      running = false;
      if (pending) {
        pending = false;
        reconcile();
      }
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, SETTLE_MS);
  }

  /* The theme publishes cartUpdate from exactly two places — the cart page and
     the product form — so subscribing to it would miss the drawer, the colour
     swatches, the quantity stepper and every app. Watching fetch instead
     catches all of them, including code we do not own. The wrapper is a
     pass-through: it reads the URL and returns the original promise untouched. */
  const nativeFetch = window.fetch;
  window.fetch = function (input) {
    const url = String((input && input.url) || input || '');
    const result = nativeFetch.apply(this, arguments);
    if (!ours && /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
      result.then(schedule).catch(() => {});
    }
    return result;
  };

  // Covers native form posts, direct links and carts carried over from an
  // earlier session. An empty cart has nothing to reconcile.
  if (config.itemCount > 0) schedule();
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && config.itemCount > 0) schedule();
  });
})();
